import React, { ReactElement } from 'react';
import { useHistory } from 'react-router';
import moment from 'moment';
import { ApolloError } from '@apollo/client';
import {
  Filter,
  Table,
  TableContainer,
  TableProps,
} from '@odin-labs/components';
import {
  GetSchedulesDocument,
  ScheduleStatus,
  useChangeScheduleStatusMutation,
  useGetSchedulesQuery,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { Container } from 'components/container';
import { NewHeader } from 'components/header/NewHeader';
import { LoadingError } from 'components/loadingError';
import { ImportScheduleModal } from 'containers/schedules/modals/ImportScheduleModal';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import {
  getCurrentISOFormattedDate,
  shouldOpenInNewTab,
  useModalState,
} from 'utils';
import { paginationSizePerPage as limit } from 'utils/constants';
import { getGraphQLError } from 'utils/error';
import { getJobsiteIdsForDevelopers } from 'utils/filters';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { ScheduleNotificationsModal } from 'containers/jobsite/tabs/jobsiteSchedules/modals/scheduleNotifications';
import { getISOFormattedDate } from 'utils/dates';
import { toOptionalBoolean } from 'utils/booleans';
import { getColumns, getFilterItems } from './SchedulesContainer.tables';
import { Schedule, ScheduleModalValue, SchedulesFilters } from './types';

export function SchedulesContainer(): ReactElement {
  const history = useHistory();
  const today = moment(getCurrentISOFormattedDate());

  const {
    page,
    developerIds,
    jobsiteIds,
    schedulerAccountIds,
    notificationsSent,
    scheduleStatuses,
    startDate,
    endDate,
    loading: isUrlLoading,
    updateUrl,
  } = usePageQueryParams({
    updateJobsiteSelection: true,
    defaultValues: {
      startDate: today,
      endDate: today,
    },
  });

  const offset = page * limit;

  const {
    value: scheduleModalValue,
    isModalOpen,
    openModal,
    closeModal,
    resetModalValue,
  } = useModalState<ScheduleModalValue>(null);
  const { modalAction } = scheduleModalValue ?? {};

  const [changeScheduleStatus] = useChangeScheduleStatusMutation({
    refetchQueries: [GetSchedulesDocument],
    onCompleted: () => {
      AlertService.alert(
        'success',
        'Success',
        'Schedule status successfully updated',
      );
    },
    onError: (error: ApolloError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
  });

  const updateScheduleStatus = React.useCallback(
    async (schedule: Schedule, status: ScheduleStatus) => {
      await changeScheduleStatus({
        variables: {
          input: {
            id: schedule.id,
            status,
          },
        },
      });
    },
    [changeScheduleStatus],
  );

  const navigateTo = React.useCallback(
    (path: string, openInNewTab: boolean): void => {
      if (openInNewTab) {
        window.open(path, '_blank');
      } else {
        history.push(path);
      }
    },
    [history],
  );

  const navigateToJobsiteSchedulesTab = React.useCallback(
    (jobsiteId: string, scheduleDate: string, openInNewTab: boolean): void => {
      navigateTo(
        `/jobsite/${jobsiteId}/schedules?date=${scheduleDate}`,
        openInNewTab,
      );
    },
    [navigateTo],
  );

  const completeSchedule = React.useCallback(
    async (schedule: Schedule) => {
      await updateScheduleStatus(schedule, ScheduleStatus.Completed);
    },
    [updateScheduleStatus],
  );

  const editSchedule = React.useCallback(
    async (schedule: Schedule) => {
      await updateScheduleStatus(schedule, ScheduleStatus.InProgress);
    },
    [updateScheduleStatus],
  );

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({
      page: pageIndex ? pageIndex + 1 : null,
    });
  };

  const onRowClickHandler: TableProps<Schedule>['onRowClick'] = ({
    data: schedule,
    event,
  }): void => {
    navigateToJobsiteSchedulesTab(
      schedule.jobsite.jobsiteId,
      schedule.date,
      shouldOpenInNewTab(event),
    );
  };

  const {
    jobsites,
    loading: userJobsitesLoading,
    error: userJobsitesError,
  } = useAvailableJobsites();

  const {
    data,
    loading: schedulesLoading,
    error: schedulesError,
    refetch: refetchSchedules,
  } = useGetSchedulesQuery({
    fetchPolicy: 'no-cache',
    skip: isUrlLoading,
    notifyOnNetworkStatusChange: true,
    variables: {
      schedulesInput: {
        paginationInput: { limit, offset },
        jobsiteIds,
        schedulerAccountIds,
        notificationsSent: toOptionalBoolean(notificationsSent),
        statuses: scheduleStatuses,
        startDate: getISOFormattedDate(startDate),
        endDate: getISOFormattedDate(endDate),
      },
    },
  });
  const { user: userData } = data?.getCurrentSession ?? {};

  const schedules = userData?.schedules.edges.map(({ node }) => node);
  const schedulesCount = userData?.schedules.count ?? 0;
  const pageCount = Math.ceil(schedulesCount / limit);
  const { filtersOptions } = userData?.schedules ?? {};

  const columns = React.useMemo(
    () =>
      getColumns({
        completeSchedule,
        editSchedule,
        navigateToJobsiteSchedulesTab,
        openModal,
      }),
    [completeSchedule, editSchedule, navigateToJobsiteSchedulesTab, openModal],
  );

  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        jobsites,
        jobsiteIds,
        developerIds,
        filtersOptions,
        notificationsSent,
        scheduleStatuses,
        schedulerAccountIds,
        startDate,
        endDate,
        today,
      }),
    [
      filtersOptions,
      jobsites,
      jobsiteIds,
      developerIds,
      schedulerAccountIds,
      startDate,
      endDate,
      notificationsSent,
      scheduleStatuses,
      today,
    ],
  );

  const onFilterChangeHandler = (
    changedFilters: Partial<SchedulesFilters>,
  ): void => {
    const { dateRange: dateRangeFilter, ...restChangedFilters } =
      changedFilters;
    const dateRange =
      dateRangeFilter === null
        ? { startDate: null, endDate: null }
        : dateRangeFilter;

    updateUrl({
      page: null,
      ...dateRange,
      ...restChangedFilters,
      ...getJobsiteIdsForDevelopers(jobsites, changedFilters.developerIds),
    });
  };

  const loading = schedulesLoading || userJobsitesLoading;
  const error = schedulesError || userJobsitesError;

  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Container className="schedules-container">
      <NewHeader
        title="Schedules"
        actionsProps={{
          onReloadPressed: (): void => {
            void refetchSchedules();
          },
        }}
      />
      <TableContainer>
        <div className="odin-w-full odin-flex odin-gap-4">
          <Filter
            items={filterItems}
            loading={loading}
            onChange={onFilterChangeHandler}
          />
        </div>
        <Table
          loading={loading}
          columns={columns}
          data={schedules}
          initialState={{ pageSize: limit, sortBy: [] }}
          pageCount={pageCount}
          pageIndex={page}
          remote
          enablePagination
          onRowClick={onRowClickHandler}
          onPageChange={onPageChangeHandler}
          disableGlobalFilter
          disableSortBy
        />
      </TableContainer>
      <ImportScheduleModal
        isOpen={isModalOpen && modalAction === 'importSchedule'}
        closeModal={closeModal}
        onClosed={resetModalValue}
        selectedSchedule={scheduleModalValue?.selectedSchedule}
        refetchQuery={GetSchedulesDocument}
      />
      <ScheduleNotificationsModal
        isOpen={isModalOpen && modalAction === 'sendNotifications'}
        closeModal={closeModal}
        onClosed={resetModalValue}
        scheduleId={scheduleModalValue?.selectedSchedule?.id}
        refetchQuery={GetSchedulesDocument}
      />
    </Container>
  );
}
